import { useState, type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { monadClient } from '../api/client'
import { Cell } from './Cell'
import { projectionFor, runProjection, type ProjectionResult } from './display/projections'
import { resolveWidget } from './registry'
import { ChipLabel } from './ChipLabel'
import { EmptyValue, isEmptyCell } from './EmptyValue'

type ReferenceProjection = Extract<Exclude<ProjectionResult, null>, { kind: 'reference-by-key' }>

type ProjectionCellProps = {
  slug?: string
  prefix?: ReactNode
  onActivate?: () => void
  activateOn?: 'click' | 'dblclick'
}

function ReferenceByKeyCell({ reference, slug, prefix, onActivate, activateOn }: { reference: ReferenceProjection } & ProjectionCellProps) {
  const { data, isLoading } = useQuery({
    queryKey: ['chip-reference', slug, reference.targetType, reference.key, reference.value],
    queryFn: () => monadClient.getChips(slug!, [], [{
      target_type: reference.targetType,
      key: reference.key,
      value: reference.value,
    }]),
    enabled: !!slug,
    staleTime: 60_000,
  })
  const resolved = data?.references?.[0]
  return (
    <Cell prefix={prefix} onActivate={onActivate} activateOn={activateOn}>
      {resolved
        ? <ChipLabel {...resolved.chip} />
        : <span className="text-muted-foreground">{isLoading ? '…' : reference.text}</span>}
    </Cell>
  )
}

function ProjectedCell({ result, slug, prefix, onActivate, activateOn }: { result: Exclude<ProjectionResult, null> } & ProjectionCellProps) {
  if (result.kind === 'reference-by-key') return <ReferenceByKeyCell reference={result} slug={slug} prefix={prefix} onActivate={onActivate} activateOn={activateOn} />
  return (
    <Cell prefix={prefix} onActivate={onActivate} activateOn={activateOn}>
      <span className={result.kind === 'number' ? 'block text-right tabular-nums' : undefined}>{result.text}</span>
    </Cell>
  )
}

/**
 * Dispatch: attribute KIND → content renderer, always wrapped in the generic
 * Cell container. This is the single seam every column type flows through.
 * An optional `prefix` is forwarded into the Cell so adornments (e.g. the
 * primary column's type glyph) live inside the same interactive box.
 *
 * Editing: when `onEdit` is supplied AND the widget registers a `write` editor,
 * the cell is editable — double-click swaps the read content for the editor.
 * The editor produces the next value; `onEdit` performs the actual persist +
 * cache reconcile (owned by the table, not the cell).
 */
export function RenderedCell({
  kind,
  renderHint,
  value,
  prefix,
  onEdit,
  config,
  slug,
  typeId,
  displaySpec,
  subFieldKinds,
  attributeId,
  format,
  emptyReason,
}: {
  kind: string
  /** SEN-753: render dispatch fallback for tenant subtypes whose kind has no own widget. */
  renderHint?: string | null
  value: unknown
  prefix?: ReactNode
  onEdit?: (next: unknown) => void
  config?: Record<string, unknown>
  /** Tenant slug — forwarded to editors that fetch (entity-reference, select). */
  slug?: string
  /** Type id — forwarded to the select editor for /select-options fetch (SEN-701). */
  typeId?: string
  /** Attribute id — forwarded to the select editor for /select-options fetch (SEN-701). */
  attributeId?: string
  /** Declared display projections for composite kinds (option A 2026-08-19).
   *  When the kind has no registered widget but carries a declaration, the
   *  default projection renders instead of 'unsupported' — data-driven, no
   *  per-kind React. */
  displaySpec?: Record<string, unknown> | null
  /** Codec kinds keyed by composite sub-field name. */
  subFieldKinds?: Record<string, string>
  /** SEN-1766: the column entry's presentation variant. Forwarded to the read
   *  widget, which draws its default for an unknown one (annotate-not-block). */
  format?: string | null
  /** The attribute's served reason for an empty value (`display.empty_reason`): an empty cell is "—" with it. */
  emptyReason?: string | null
}) {
  const spec = resolveWidget(kind, renderHint)
  const [editing, setEditing] = useState(false)
  // Editable if the table wired onEdit AND the widget knows how to change:
  // a `toggle` (direct flip, e.g. boolean) or a `write` overlay editor.
  const canEdit = !!onEdit && !!(spec?.toggle || spec?.write)

  function handleActivate() {
    if (!onEdit || !spec) return
    if (spec.toggle) onEdit(spec.toggle(value))
    else if (spec.write) setEditing(true)
  }

  if (editing && spec?.write && onEdit) {
    const Editor = spec.write
    return (
      <Editor
        value={value}
        config={config}
        slug={slug}
        typeId={typeId}
        attributeId={attributeId}
        onCommit={(next) => {
          setEditing(false)
          if (next !== value) onEdit(next)
        }}
        onCancel={() => setEditing(false)}
      />
    )
  }

  if (emptyReason && isEmptyCell(value)) {
    return (
      <Cell prefix={prefix} onActivate={canEdit ? handleActivate : undefined} activateOn={spec?.toggle ? 'click' : 'dblclick'}>
        <EmptyValue reason={emptyReason} />
      </Cell>
    )
  }

  // A declaration owns composite READ presentation even when the kind also
  // registers a write editor. Dispatch it before the widget read path so
  // location/address/name cannot retain a second hand-written formatter.
  const projection = projectionFor(displaySpec, format, subFieldKinds)
  if (projection) {
    if (value != null) {
      const r = runProjection(value, projection)
      if (r) return <ProjectedCell
        result={r}
        slug={slug}
        prefix={prefix}
        onActivate={canEdit ? handleActivate : undefined}
        activateOn={spec?.toggle ? 'click' : 'dblclick'}
      />
    }
    // A named variant is authoritative even when empty. For the default
    // projection, let a registered widget handle richer fallbacks (for
    // example a coordinate-only location) before showing an empty value.
    if (format || !spec) {
      return (
        <Cell prefix={prefix} onActivate={canEdit ? handleActivate : undefined} activateOn={spec?.toggle ? 'click' : 'dblclick'}>
          <span className="text-muted-foreground">—</span>
        </Cell>
      )
    }
  }

  if (!spec) {
    return (
      <Cell prefix={prefix}>
        <span className="italic text-muted-foreground">unsupported: {kind}</span>
      </Cell>
    )
  }

  const Content = spec.read
  return (
    <Cell
      align={spec.align}
      prefix={prefix}
      onActivate={canEdit ? handleActivate : undefined}
      // boolean (toggle) flips on a single click; overlay editors open on double-click
      activateOn={spec.toggle ? 'click' : 'dblclick'}
    >
      <Content value={value} config={config} format={format} />
    </Cell>
  )
}
